/**
 * Les gestes de la file des dépenses à placer.
 *
 * Le miroir de `actions.supabase.ts`, avec **une différence de posture qui
 * change tout** : à l'entrée, la question est « quelle facture ? » et l'action
 * est annulable ; à la sortie, **l'argent est déjà parti**. Défaire une
 * allocation ne le ramène pas — cela ne fait que dire qu'on s'était trompé de
 * pièce. L'écran doit le montrer, et ces fonctions ne prétendent jamais le
 * contraire.
 *
 * ## Une seule porte pour placer un virement
 *
 * `placerRemboursement` écrit TOUTES les lignes d'un placement en une
 * insertion. C'est la leçon des 25,00 € disparus le 4 septembre 2026, côté
 * entrant : deux boutons écrivaient chacun leur moitié en ignorant l'autre.
 * Une seule porte, donc, et les invariants de la base font le reste.
 *
 * ## Ce que la base refuse, et qui remonte tel quel
 *
 * - **Invariant 1'** — placer au-delà du montant du virement.
 * - **Invariant 2'** — placer au-delà de ce que vaut une note de crédit. Ce
 *   refus-là n'est pas théorique : `NC-26-00455`, note de 22,00 €, a reçu deux
 *   virements de 22,00 € à un mois d'intervalle. L'erreur est déjà dans les
 *   données ; l'invariant empêche la suivante.
 *
 * Ces erreurs ne sont jamais avalées : elles remontent à l'écran en français
 * approximatif de PostgreSQL, et c'est voulu — une erreur qu'on ne voit pas
 * est une erreur qu'on refait.
 */

import { db } from './db';
import { NUMERO_HISTORIQUE } from './matching';
import type { ProvisionDeFamille } from './provisionARendre';
import type { EncaissementDeFacture } from './annulerLEncaissement';
import { normaliserIban, type CompteDeFamille } from './compteDuBeneficiaire';
import { messageErreur } from './erreur';
import type { NoteResolue, ReferencesDuVirement } from './sortie';
import { destineeDeLaNote, type Destinee } from '../creditNotes/destinee';

/** Une ligne de la file : un virement émis que l'automatique n'a pas placé. */
export interface DepenseAPlacer {
  id: string;
  valueDate: string;
  amountCents: number;
  unallocatedCents: number;
  state: 'pending' | 'partial' | 'allocated';
  communicationRaw: string | null;
  communicationKind: 'structured' | 'legacy' | 'unstructured' | null;
  legacyInvoiceRef: string | null;
  structuredDigits: string | null;
  freeText: string | null;
  possiblyTruncated: boolean;
  counterpartyIban: string | null;
  counterpartyName: string | null;
  userId: string | null;
  /** Les numéros de note déjà placés sur ce virement, pour l'affichage. */
  notesPlacees: string | null;
}

/**
 * Combien de dépenses attendent d'être placées, et pour quel montant.
 *
 * Le compteur du badge. `count: 'exact'` avec `head: true` demande le nombre
 * sans rapatrier une ligne — la même précaution que côté entrant, où compter
 * en chargeant la file faisait annoncer « 1000 », qui n'était pas un nombre de
 * lignes mais le plafond de PostgREST.
 *
 * La file du sortant est petite : 14 virements en attente et 6 partiels au
 * 13 septembre 2026, pour 1 179,00 €. Le compteur reste néanmoins exact, parce
 * qu'elle grossira à chaque import.
 */
export async function countDepensesAPlacer(): Promise<{ nombre: number }> {
  const { count, error } = await db
    .from('v_refund_allocation_state')
    .select('refund_id', { count: 'exact', head: true })
    .neq('state', 'allocated');
  if (error) throw error;
  return { nombre: count ?? 0 };
}

/**
 * La file, triée du plus récent au plus ancien.
 *
 * Le tri se fait **en base** : trier une page déjà reçue ne trierait que la
 * page, ce qui se voit dès la seconde.
 */
export async function loadDepensesAPlacer(
  options: { limite?: number; depuis?: number } = {},
): Promise<DepenseAPlacer[]> {
  const limite = options.limite ?? 100;
  const depuis = options.depuis ?? 0;

  const { data, error } = await db
    .from('v_refund_allocation_state')
    .select(
      'refund_id, value_date, amount_cents, allocated_cents, unallocated_cents, state, '
      + 'communication_raw, communication_kind, legacy_invoice_ref, structured_digits, '
      + 'free_text, possibly_truncated, counterparty_iban, counterparty_name, user_id, notes_placees',
    )
    .neq('state', 'allocated')
    .order('value_date', { ascending: false })
    .range(depuis, depuis + limite - 1);
  if (error) throw error;

  /* `as unknown as` parce que les deux vues du sortant ne sont pas encore dans
     les types generes par Supabase : sans ce passage, leur resultat est type
     `GenericStringError`. Meme forme que `versRef` dans gateway.supabase.ts. */
  return ((data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r.refund_id),
    valueDate: String(r.value_date),
    amountCents: Number(r.amount_cents),
    unallocatedCents: Number(r.unallocated_cents),
    state: r.state as DepenseAPlacer['state'],
    communicationRaw: (r.communication_raw as string) ?? null,
    communicationKind: (r.communication_kind as DepenseAPlacer['communicationKind']) ?? null,
    legacyInvoiceRef: (r.legacy_invoice_ref as string) ?? null,
    structuredDigits: (r.structured_digits as string) ?? null,
    freeText: (r.free_text as string) ?? null,
    possiblyTruncated: Boolean(r.possibly_truncated),
    counterpartyIban: (r.counterparty_iban as string) ?? null,
    counterpartyName: (r.counterparty_name as string) ?? null,
    userId: (r.user_id as string) ?? null,
    notesPlacees: (r.notes_placees as string) ?? null,
  }));
}

/** Une note de crédit proposée au choix de l'humain. */
export interface NoteARembourser {
  id: string;
  number: string;
  userId: string | null;
  amountCents: number;
  refundedCents: number;
  refundableCents: number;
  invoiceNumber: string | null;
  invoiceId: string | null;
  /** Ce que la facture a encaissé selon le NOUVEAU modèle. */
  factureEncaisseCents: number | null;
  /** Ce que l'ANCIEN circuit connaît. Voir la note ci-dessous. */
  factureAncienCircuitCents: number | null;
  /**
   * L'argent PROUVÉ derrière cette note : le trop-perçu de sa facture, réparti
   * FIFO. Zéro veut dire « elle a réduit une facture impayée », pas « elle ne
   * vaut rien » — le rapprochement entrant n'étant pas terminé, une note sans
   * cash prouvé aujourd'hui peut en avoir demain.
   */
  cashAdosseCents: number;
  /** `deduite`, `a_rendre`, `partiellement_rendue`, `rendue`. */
  destinee: Destinee;
  /** Ce qu'il reste à rendre en argent : jamais plus que le cash prouvé. */
  aRendreCents: number;
}

/**
 * Cherche une note de crédit à rembourser.
 *
 * ## Pourquoi la recherche n'exclut PAS les notes dont la facture paraît impayée
 *
 * Parce que l'état comptable de la facture **ne prédit pas** la remboursabilité,
 * et que filtrer là-dessus cacherait la moitié des cas légitimes.
 *
 * Mesuré le 12 septembre 2026 : `v_invoice_balances.overpaid_cents` vaut **0
 * sur les 3 714 factures** — non pas faute de trop-perçu, mais parce que
 * l'invariant 2 plafonne l'affectation au net et que l'excédent reste non
 * affecté sur le paiement. Et **18 des 36 notes réellement remboursées, soit la
 * moitié, portent sur une facture à `allocated_cents = 0`**.
 *
 * Le rapprochement des paiements entrants n'étant pas terminé — 5 528 paiements
 * n'ont encore aucune allocation — une note peut parfaitement rembourser de
 * l'argent versé et pas encore encodé. L'écran montre donc les deux chiffres et
 * laisse l'humain juger, au lieu de trancher à sa place sur une donnée qu'il
 * sait incomplète.
 */
export async function chercherNotesARembourser(
  recherche: string,
  limite = 25,
): Promise<NoteARembourser[]> {
  const terme = recherche.trim();
  if (terme.length < 2) return [];

  let q = db
    .from('v_credit_note_balances')
    .select(
      'credit_note_id, credit_note_number, user_id, amount_cents, refunded_cents, '
      + 'refundable_cents, invoice_number, invoice_id, facture_encaisse_cents, '
      + 'facture_ancien_circuit_cents, cash_adosse_cents, destinee, a_rendre_cents',
    )
    .gt('refundable_cents', 0)
    /* Celles qui ont de l'argent derrière elles d'abord : ce sont les seules
       qui appellent vraiment un virement. Les déductions restent visibles —
       elles ne sont pas cachées — mais elles passent après. */
    .order('a_rendre_cents', { ascending: false })
    .order('credit_note_number', { ascending: false })
    .limit(limite);

  /* Le joker de PostgREST est `*`, converti en `%` côté SQL. Écrire `%`
     directement marche par accident selon l'encodage de l'URL. */
  const motif = `*${terme}*`;
  q = q.or(`credit_note_number.ilike.${motif},invoice_number.ilike.${motif}`);

  const { data, error } = await q;
  if (error) throw error;

  return ((data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r.credit_note_id),
    number: String(r.credit_note_number),
    userId: (r.user_id as string) ?? null,
    amountCents: Number(r.amount_cents),
    refundedCents: Number(r.refunded_cents),
    refundableCents: Number(r.refundable_cents),
    invoiceNumber: (r.invoice_number as string) ?? null,
    invoiceId: (r.invoice_id as string) ?? null,
    factureEncaisseCents: r.facture_encaisse_cents === null ? null : Number(r.facture_encaisse_cents),
    factureAncienCircuitCents:
      r.facture_ancien_circuit_cents === null ? null : Number(r.facture_ancien_circuit_cents),
    cashAdosseCents: Number(r.cash_adosse_cents ?? 0),
    /* La destinée est recalculée ici plutôt que lue telle quelle : la règle vit
       dans `destinee.ts`, elle y est testée, et deux definitions qui divergent
       finiraient par se contredire — c'est exactement ce qui est arrivé au
       prix, calculé a la fois dans le navigateur et en base. */
    destinee: destineeDeLaNote({
      amountCents: Number(r.amount_cents),
      refundedCents: Number(r.refunded_cents),
      cashAdosseCents: Number(r.cash_adosse_cents ?? 0),
    }),
    aRendreCents: Number(r.a_rendre_cents ?? 0),
  }));
}

/** Une ligne de placement, telle que l'écran la compose. */
export interface LigneDePlacement {
  target: 'credit_note' | 'invoice' | 'provision' | 'out_of_scope';
  amountCents: number;
  creditNoteNumber?: string | null;
  creditNoteId?: string | null;
  invoiceId?: string | null;
  provisionUserId?: string | null;
}

/**
 * Place un virement émis, en une seule insertion.
 *
 * `method` vaut toujours `manual` ici : c'est un humain qui a décidé, y compris
 * — et surtout — sur un mouvement que **rien n'annotait**. C'est le rattrapage
 * du cas rare où l'administration a oublié d'écrire le numéro de note : il est
 * rare, il n'est pas impossible, et sans ce geste le virement resterait dans la
 * file pour toujours.
 *
 * Renseigne aussi `refunds.user_id` quand le placement désigne une famille sans
 * ambiguïté — c'est cette colonne que `v_user_provision_balances` déduit, et
 * elle n'a jamais été écrite sur les 45 lignes existantes.
 */
export async function placerRemboursement(
  refundId: string,
  lignes: LigneDePlacement[],
  options: { userId?: string | null; notes?: string } = {},
): Promise<void> {
  if (lignes.length === 0) return;

  const { error } = await db.from('refund_allocations').insert(
    lignes.map((l) => ({
      refund_id: refundId,
      amount_cents: l.amountCents,
      target: l.target,
      credit_note_number: l.creditNoteNumber ?? null,
      credit_note_id: l.creditNoteId ?? null,
      invoice_id: l.invoiceId ?? null,
      provision_user_id: l.provisionUserId ?? null,
      method: 'manual',
      notes: options.notes ?? null,
    })),
  );
  if (error) throw error;

  if (options.userId) {
    const { error: erreurFamille } = await db
      .from('refunds')
      .update({ user_id: options.userId })
      .eq('id', refundId);
    if (erreurFamille) throw erreurFamille;
  }
}

/**
 * Classe une dépense hors du périmètre des familles.
 *
 * Volontariat, frais de déplacement, salaires, fournisseurs. Le mouvement est
 * **lu, classé et compté** — jamais effacé, et jamais rattaché à une famille.
 * C'est ce qui rendra le total débit du relevé rapprochable : `parseCoda` le
 * calcule déjà et rien ne l'exploitait.
 */
export async function classerHorsPerimetre(refundId: string, notes?: string): Promise<void> {
  const { data, error } = await db
    .from('refunds')
    .select('amount_cents, allocated_cents')
    .eq('id', refundId)
    .single();
  if (error) throw error;

  const reste = Number(data.amount_cents) - Number(data.allocated_cents);
  if (reste <= 0) return;

  const { error: erreurInsertion } = await db.from('refund_allocations').insert({
    refund_id: refundId,
    amount_cents: reste,
    target: 'out_of_scope',
    method: 'manual',
    notes: notes ?? null,
  });
  if (erreurInsertion) throw erreurInsertion;
}

/** Ce qui a déjà été placé sur un virement. */
export interface PlacementExistant {
  id: string;
  amountCents: number;
  target: string;
  creditNoteNumber: string | null;
  invoiceId: string | null;
  method: string;
  notes: string | null;
  createdAt: string;
}

export async function loadPlacements(refundId: string): Promise<PlacementExistant[]> {
  const { data, error } = await db
    .from('refund_allocations')
    .select('id, amount_cents, target, credit_note_number, invoice_id, method, notes, created_at')
    .eq('refund_id', refundId)
    .order('created_at', { ascending: true });
  if (error) throw error;

  return ((data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r.id),
    amountCents: Number(r.amount_cents),
    target: String(r.target),
    creditNoteNumber: (r.credit_note_number as string) ?? null,
    invoiceId: (r.invoice_id as string) ?? null,
    method: String(r.method),
    notes: (r.notes as string) ?? null,
    createdAt: String(r.created_at),
  }));
}

/**
 * Défait un placement.
 *
 * **Ne rend pas l'argent.** Le virement est parti ; défaire ne fait que dire
 * qu'on s'était trompé de pièce, et remet le montant dans la file. L'écran doit
 * l'écrire noir sur blanc, sans quoi « Défaire » se lit comme « annuler le
 * virement ».
 */
export async function defairePlacement(allocationId: string): Promise<void> {
  const { error } = await db.from('refund_allocations').delete().eq('id', allocationId);
  if (error) throw error;
}

/**
 * Les notes de crédit qu'un virement sortant cite — y compris celles qu'il ne
 * cite que par leur FACTURE.
 *
 * ## Le cas qui a fait écrire cette fonction
 *
 * 165,00 € virés le 02/09/2026, communication coupée à 53 caractères :
 *
 *     CDV-NDSC-2601711 REMBOURSEMENT NC-26-00479 et NC-26-0
 *
 * Le second numéro est tranché en plein milieu. La facture, elle, est entière
 * et porte deux notes : `NC-26-00479`, déjà remboursée, et `NC-26-00478`,
 * 92,00 € encore à rendre. **Lire la facture, c'est retrouver la note que la
 * troncature a emportée.**
 *
 * ## Ce que cette lecture ne filtre pas
 *
 * Elle rend les notes **déjà remboursées** comme les autres. Une note soldée
 * n'est pas une note absente : c'est la réponse à « pourquoi ce virement ne
 * trouve-t-il rien ? », et la taire laisse l'administration devant un écran
 * vide. La colonne `remboursable` dit ce que chacune peut encore recevoir.
 *
 * Elle ne regarde pas non plus si la famille a une dette. Demandé le
 * 19 septembre 2026 : « Même si l'utilisateur a une dette, il faut que l'admin
 * puisse quand même rapprocher le remboursement vu qu'il a bel et bien été fait
 * en banque. » Le virement est parti ; le rapprochement ne fait que dire à
 * quelle pièce il correspond.
 */
export async function lireNotesCitees(
  refs: ReferencesDuVirement,
  refundId: string,
): Promise<NoteResolue[]> {
  const numeros = refs.notes.map((n) => n.numero);
  /* Le numéro entier quand la forme historique le donne : voir `NUMERO_HISTORIQUE`. */
  const chiffres = refs.factures.map((f) => f.numeroComplet ?? f.digits);
  if (numeros.length === 0 && chiffres.length === 0) return [];

  const clauses = [
    ...numeros.map((n) => `credit_note_number.eq.${n}`),
    ...chiffres.map((d) => (NUMERO_HISTORIQUE.test(d)
      ? `invoice_number.eq.${d}`
      : `invoice_number.ilike.*${d}*`)),
  ];

  const { data, error } = await db
    .from('v_credit_note_balances')
    .select(
      'credit_note_id, credit_note_number, user_id, amount_cents, refunded_cents, '
      + 'refundable_cents, invoice_number, destinee',
    )
    .or(clauses.join(','))
    .limit(60);
  if (error) throw error;

  const lignes = (data ?? []) as unknown as {
    credit_note_id: string; credit_note_number: string; user_id: string | null;
    amount_cents: number; refunded_cents: number; refundable_cents: number;
    invoice_number: string | null; destinee: string | null;
  }[];

  /* Ce que CE virement a déjà donné, note par note. Sans cette colonne, une
     note soldée à l'instant par ce virement même se lit « déjà remboursée
     ailleurs » — et on défait ce qu'on vient de faire. */
  const dejaDonne = new Map<string, number>();
  const { data: placements, error: erreurPlacements } = await db
    .from('refund_allocations')
    .select('credit_note_id, credit_note_number, amount_cents')
    .eq('refund_id', refundId);
  if (erreurPlacements) throw erreurPlacements;
  for (const p of (placements ?? []) as unknown as {
    credit_note_id: string | null; credit_note_number: string | null; amount_cents: number;
  }[]) {
    const cle = p.credit_note_id ?? p.credit_note_number;
    if (!cle) continue;
    dejaDonne.set(cle, (dejaDonne.get(cle) ?? 0) + Number(p.amount_cents));
  }

  const noms = await nomsDeFamille(
    [...new Set(lignes.map((l) => l.user_id).filter((u): u is string => !!u))],
  );

  const versNote = (
    l: (typeof lignes)[number],
    origine: NoteResolue['origine'],
    parLaFacture: string | null,
  ): NoteResolue => ({
    numero: l.credit_note_number,
    origine,
    parLaFacture,
    creditNoteId: l.credit_note_id,
    userId: l.user_id,
    famille: l.user_id ? noms.get(l.user_id) ?? 'Compte sans nom' : null,
    invoiceNumber: l.invoice_number,
    montantCents: Number(l.amount_cents),
    dejaRembourseCents: Number(l.refunded_cents),
    remboursableCents: Number(l.refundable_cents),
    deCeVirementCents:
      dejaDonne.get(l.credit_note_id) ?? dejaDonne.get(l.credit_note_number) ?? 0,
    destinee: l.destinee,
  });

  const sortie: NoteResolue[] = [];
  const placees = new Set<string>();

  /* D'abord les notes nommées, dans l'ordre où elles sont citées. */
  for (const citee of refs.notes) {
    const l = lignes.find((x) => x.credit_note_number === citee.numero);
    placees.add(citee.numero);
    sortie.push(
      l
        ? versNote(l, citee.origine, null)
        : {
          numero: citee.numero, origine: citee.origine, parLaFacture: null,
          creditNoteId: null, userId: null, famille: null, invoiceNumber: null,
          montantCents: 0, dejaRembourseCents: 0, remboursableCents: 0,
          deCeVirementCents: 0, destinee: null,
        },
    );
  }

  /* Puis celles que seule la FACTURE citée désigne : la découverte utile. */
  for (const f of refs.factures) {
    for (const l of lignes.filter((x) => (f.numeroComplet
      ? (x.invoice_number ?? '') === f.numeroComplet
      : (x.invoice_number ?? '').includes(f.digits)))) {
      if (placees.has(l.credit_note_number)) continue;
      placees.add(l.credit_note_number);
      sortie.push(versNote(l, f.origine, l.invoice_number));
    }
  }

  return sortie;
}

async function nomsDeFamille(userIds: string[]): Promise<Map<string, string>> {
  const noms = new Map<string, string>();
  if (userIds.length === 0) return noms;
  const { data, error } = await db.from('users').select('id, nom, prenom').in('id', userIds);
  if (error) throw error;
  for (const u of (data ?? []) as unknown as {
    id: string; nom: string | null; prenom: string | null;
  }[]) {
    noms.set(u.id, [u.prenom, u.nom].filter(Boolean).join(' ').trim() || 'Compte sans nom');
  }
  return noms;
}

/**
 * Les familles a qui ce virement peut rendre une PROVISION.
 *
 * Passe par les factures citees, et non par les notes de credit : un double
 * paiement n'en produit aucune. La facture donne la famille, la famille donne
 * son avoir.
 *
 * `avoir_cents` et non `available_cents` : le second est garde par le reglage
 * `provision_auto`, qui vaut `off`, et vaut donc zero pour les 149 familles
 * qui ont pourtant un avoir. Le lire ici afficherait « aucune provision »
 * devant 9 443,45 EUR bien reels.
 */
export async function lireLesProvisionsCitees(
  refs: ReferencesDuVirement,
): Promise<ProvisionDeFamille[]> {
  const numeros = refs.factures.map((f) => f.numeroComplet ?? f.digits);
  if (numeros.length === 0) return [];

  const { data: lignes, error } = await db
    .from('v_admin_factures')
    .select('invoice_id, invoice_number, user_id')
    .or(numeros.map((d) => (NUMERO_HISTORIQUE.test(d)
      ? `invoice_number.eq.${d}`
      : `invoice_number.ilike.*${d}*`)).join(','))
    .limit(50);
  if (error) throw error;

  const factures = (lignes ?? []) as unknown as {
    invoice_id: string; invoice_number: string; user_id: string | null;
  }[];
  const parFacture = new Map<string, string>();
  for (const f of factures) {
    if (f.user_id && !parFacture.has(f.user_id)) parFacture.set(f.user_id, f.invoice_number);
  }
  return chargerLesProvisions([...parFacture.keys()], parFacture);
}

/**
 * Les provisions de familles designees AUTREMENT que par une piece citee.
 *
 * ## Pourquoi ce second chemin existe
 *
 * Signale le 20 septembre 2026 sur un virement de 60,00 EUR a Sarah Lacour :
 * « il reconnait le compte mais automatiquement il met un numero de facture
 * dans la recherche a la main, ce qui casse le lien avec le compte ».
 *
 * La communication disait `CDV-2506002-00072` — `250602` avec un zero de trop.
 * Aucune facture ne porte ce numero, donc aucune famille n'en sortait, donc
 * aucune provision n'etait proposee : il ne restait que la recherche a la main.
 * Le compte bancaire, lui, etait formellement reconnu — six versements en
 * proviennent, tous de cette famille — et l'ecran l'affichait sans rien en
 * faire.
 *
 * Une reference mal frappee est une raison de plus de se fier au compte, pas
 * une raison de se taire.
 */
export async function lireLesProvisionsDesComptes(
  userIds: string[],
): Promise<ProvisionDeFamille[]> {
  return chargerLesProvisions(userIds, new Map());
}

/**
 * L'avoir, le nom et l'origine d'une provision, pour des familles deja
 * identifiees. Le socle commun des deux chemins ci-dessus.
 */
async function chargerLesProvisions(
  userIds: string[],
  parFacture: Map<string, string>,
): Promise<ProvisionDeFamille[]> {
  if (userIds.length === 0) return [];

  const [avoirs, noms, origines] = await Promise.all([
    db.from('v_user_provision_balances').select('user_id, avoir_cents').in('user_id', userIds),
    db.from('users').select('id, prenom, nom, email').in('id', userIds),
    /* Ce qui a CONSTITUE la provision. Une provision affichee sans son origine
       se croit sur parole ; avec elle, l'administration reconnait le geste. */
    db.from('payment_allocations')
      .select('provision_user_id, notes, amount_cents')
      .eq('target', 'provision')
      .in('provision_user_id', userIds)
      .order('amount_cents', { ascending: false })
      .limit(100),
  ]);
  for (const r of [avoirs, noms, origines]) if (r.error) throw r.error;

  const parAvoir = new Map(
    ((avoirs.data ?? []) as unknown as { user_id: string; avoir_cents: number }[])
      .map((a) => [a.user_id, Number(a.avoir_cents ?? 0)]),
  );
  const parNom = new Map(
    ((noms.data ?? []) as unknown as {
      id: string; prenom: string | null; nom: string | null; email: string | null;
    }[]).map((u) => [
      u.id,
      [u.prenom, u.nom].filter(Boolean).join(' ').trim() || u.email || u.id,
    ]),
  );
  const parOrigine = new Map<string, string>();
  for (const o of (origines.data ?? []) as unknown as {
    provision_user_id: string; notes: string | null;
  }[]) {
    if (o.notes && !parOrigine.has(o.provision_user_id)) {
      parOrigine.set(o.provision_user_id, o.notes);
    }
  }

  return userIds.map((userId) => ({
    userId,
    famille: parNom.get(userId) ?? userId,
    avoirCents: parAvoir.get(userId) ?? 0,
    parLaFacture: parFacture.get(userId) ?? null,
    origine: parOrigine.get(userId) ?? null,
  }));
}

/**
 * Ce que les factures citees par un virement sortant ont encaisse.
 *
 * ## Pourquoi cette lecture existe
 *
 * Un remboursement sans note de credit n'annule pas une creance : il annule un
 * ENCAISSEMENT. Pour le proposer, l'ecran doit savoir ce que la facture citee a
 * recu, et ce qu'un remboursement lui a deja repris. Voir
 * `annulerLEncaissement.ts` pour la lecture en partie double.
 *
 * ## L'encaisse est lu a la source, pas deduit
 *
 * `v_invoice_balances.allocated_cents` est desormais NET : encaisse moins
 * rembourse, plancher a zero. Le reconstituer par addition donnerait un chiffre
 * faux des qu'une facture a ete remboursee au-dela de ce qu'elle avait recu —
 * une anomalie, mais precisement le genre de ligne que cet ecran doit montrer
 * juste. Les imputations sont donc relues et sommees ici.
 */
export async function lireLesEncaissementsCites(
  refs: ReferencesDuVirement,
): Promise<EncaissementDeFacture[]> {
  const numeros = refs.factures.map((f) => f.numeroComplet ?? f.digits);
  if (numeros.length === 0) return [];

  const { data: lignes, error } = await db
    .from('v_invoice_balances')
    .select('invoice_id, invoice_number, user_id, amount_cents, net_amount_cents, '
      + 'credit_notes_cents, refunded_on_invoice_cents, settlement')
    /* Meme lecture que partout : egalite sur l'ancien format `CDV-YYMMDD-NNNNN`,
       dont les sept chiffres utiles ne figurent pas tels quels dans le numero. */
    .or(numeros.map((d) => (NUMERO_HISTORIQUE.test(d)
      ? `invoice_number.eq.${d}`
      : `invoice_number.ilike.*${d}*`)).join(','))
    .limit(50);
  if (error) throw error;

  const factures = (lignes ?? []) as unknown as {
    invoice_id: string; invoice_number: string; user_id: string | null;
    amount_cents: number; net_amount_cents: number; credit_notes_cents: number;
    refunded_on_invoice_cents: number; settlement: string | null;
  }[];
  if (factures.length === 0) return [];

  const invoiceIds = factures.map((f) => f.invoice_id);
  const userIds = [...new Set(factures.map((f) => f.user_id).filter(Boolean))] as string[];

  const [encaisses, noms] = await Promise.all([
    db.from('payment_allocations')
      .select('invoice_id, amount_cents')
      .in('invoice_id', invoiceIds)
      .limit(500),
    userIds.length > 0
      ? db.from('users').select('id, prenom, nom, email').in('id', userIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  for (const r of [encaisses, noms]) if (r.error) throw r.error;

  const parEncaisse = new Map<string, number>();
  for (const a of (encaisses.data ?? []) as unknown as {
    invoice_id: string; amount_cents: number;
  }[]) {
    parEncaisse.set(a.invoice_id, (parEncaisse.get(a.invoice_id) ?? 0) + Number(a.amount_cents));
  }
  const parNom = new Map(
    ((noms.data ?? []) as unknown as {
      id: string; prenom: string | null; nom: string | null; email: string | null;
    }[]).map((u) => [
      u.id,
      [u.prenom, u.nom].filter(Boolean).join(' ').trim() || u.email || u.id,
    ]),
  );

  return factures.map((f) => ({
    invoiceId: f.invoice_id,
    invoiceNumber: f.invoice_number,
    userId: f.user_id,
    famille: f.user_id ? parNom.get(f.user_id) ?? null : null,
    montantCents: Number(f.amount_cents ?? 0),
    netCents: Number(f.net_amount_cents ?? 0),
    notesCents: Number(f.credit_notes_cents ?? 0),
    encaisseCents: parEncaisse.get(f.invoice_id) ?? 0,
    dejaAnnuleCents: Number(f.refunded_on_invoice_cents ?? 0),
    reglement: f.settlement,
  }));
}

/**
 * Les comptes bancaires que la base sait relier a une famille.
 *
 * Une requete pour TOUTE la page, jamais une par ligne : la vue agrege
 * 17 717 encaissements et coute environ 550 ms, ce qui est acceptable une fois
 * et ne l'est pas cinquante fois.
 *
 * Le filtre porte sur des IBAN deja normalises — memes regles que la vue :
 * sans espace, sans tiret, en majuscules.
 */
export async function lireLesComptesDesFamilles(
  ibans: string[],
): Promise<CompteDeFamille[]> {
  const cles = [...new Set(ibans.map(normaliserIban).filter(Boolean))] as string[];
  if (cles.length === 0) return [];

  const { data, error } = await db
    .from('v_comptes_des_familles')
    .select('iban, user_id, versements, dernier_versement, nom_au_releve, partage')
    .in('iban', cles);
  if (error) throw error;

  const lignes = (data ?? []) as unknown as {
    iban: string; user_id: string; versements: number;
    dernier_versement: string | null; nom_au_releve: string | null; partage: boolean;
  }[];
  if (lignes.length === 0) return [];

  /* Le nom de la famille vient de `users`, pas du releve : c'est celui sous
     lequel l'administration la connait. Le nom bancaire, lui, est garde a
     part — il sert a reconnaitre la ligne sur l'extrait. */
  const noms = await nomsDeFamille([...new Set(lignes.map((l) => l.user_id))]);

  return lignes.map((l) => ({
    iban: l.iban,
    userId: l.user_id,
    famille: noms.get(l.user_id) ?? 'Compte sans nom',
    versements: Number(l.versements ?? 0),
    dernierVersement: l.dernier_versement,
    nomAuReleve: l.nom_au_releve,
    partage: l.partage === true,
  }));
}

/**
 * Classe PLUSIEURS dépenses hors du périmètre des familles, d'un seul geste.
 *
 * ## Pourquoi par paquets
 *
 * Mesuré le 19 septembre 2026 : la file des dépenses compte 590 mouvements
 * pour 648 205,43 €, dont **44 seulement — 3 038,20 €** — citent une famille.
 * Les 546 autres sont l'ONSS, Proximus, Ethias, Monizze, les écoles
 * partenaires, les compensations aux charges, le volontariat : de l'argent
 * qu'Éclosion dépense pour fonctionner, et qui n'a aucune contrepartie dans
 * cette comptabilité-ci.
 *
 * Les classer un par un, c'est 546 fois le même clic. Demandé tel quel :
 * « regrouper tous les mouvements de volontariat et de salaire et pouvoir les
 * écarter d'un coup ».
 *
 * Une seule insertion : ou tout est écrit, ou rien ne l'est. Un classement à
 * moitié appliqué laisserait une file dont on ne sait plus ce qu'elle contient.
 */
export async function classerPlusieursHorsPerimetre(
  refundIds: string[],
  note?: string,
): Promise<number> {
  if (refundIds.length === 0) return 0;

  const { data, error } = await db
    .from('refunds')
    .select('id, amount_cents, allocated_cents')
    .in('id', refundIds);
  if (error) throw error;

  const lignes = ((data ?? []) as unknown as {
    id: string; amount_cents: number; allocated_cents: number;
  }[])
    .map((r) => ({ id: r.id, reste: Number(r.amount_cents) - Number(r.allocated_cents) }))
    .filter((r) => r.reste > 0);
  if (lignes.length === 0) return 0;

  /*
    Par tranches de cent. Depuis que la file se selectionne d'un geste, un
    paquet peut valoir plusieurs centaines de lignes — et chacune declenche le
    recalcul de `refunds.allocated_cents`. Une insertion unique de 455 lignes
    tiendrait dans les 8 s de `statement_timeout` aujourd'hui, mais elle n'a
    aucune marge, et une insertion refusee a mi-parcours ne dit pas ou elle en
    etait. Cent a la fois, chaque tranche est une ecriture complete.
  */
  const TRANCHE = 100;
  let ecrites = 0;
  for (let i = 0; i < lignes.length; i += TRANCHE) {
    const paquet = lignes.slice(i, i + TRANCHE);
    const { error: erreurInsertion } = await db.from('refund_allocations').insert(
      paquet.map((l) => ({
        refund_id: l.id,
        amount_cents: l.reste,
        target: 'out_of_scope' as const,
        method: 'manual' as const,
        notes: note ?? null,
      })),
    );
    if (erreurInsertion) {
      /* Ce qui precede est ECRIT. Le dire, plutot que de laisser croire que
         le geste entier a echoue et pousser a le refaire en double. */
      if (ecrites > 0) {
        throw new Error(
          `${ecrites} depense(s) ecartee(s) avant l'echec ; les suivantes ne le sont pas. `
          + messageErreur(erreurInsertion),
        );
      }
      throw erreurInsertion;
    }
    ecrites += paquet.length;
  }
  return ecrites;
}
